/**
 * ClientFormModal — Modal CRUD de cliente (create / edit).
 *
 * UX (3 steps visuales — tabs navegables libremente en EDIT,
 *  flujo "mínimo y continuar" en CREATE):
 *
 *   1. Identidad         (full_name req, email, phone, photo, sex, birthdate)
 *   2. Antropométricos   (height, peso inicial / actual, cintura, cuello, cadera)
 *   3. Fitness y notas   (activity, goal, level, injuries, medical_notes,
 *                        start_date, end_date, notes)
 *   4. Medidas           (histórico de perímetros/composición — ClientMeasurementsPanel)
 *
 *   En CREATE basta con full_name para guardar (el coach puede completar el
 *   resto editando más tarde — patrón típico SaaS). En EDIT los 4 steps se
 *   acceden libremente y existe botón "Archivar" o "Restaurar" según status.
 *
 *   El step 4 (Medidas) sólo funciona cuando el cliente YA existe (tiene id):
 *   en modo CREATE muestra un placeholder pidiendo guardar primero; en modo
 *   EDIT renderiza el ClientMeasurementsPanel completo.
 *
 * Manejo de 402 (cap excedido del tier):
 *   Tanto createClient como restoreClient pueden devolver 402 con el detalle
 *   `{ error:"limit_exceeded", current, limit, tier, suggested_tier }`.
 *   Mostramos un banner inline dentro del modal con el mensaje y CTA para
 *   archivar uno o hacer upgrade. El modal NO se cierra para que el coach
 *   no pierda lo que escribió.
 *
 * Mobile: a 375px los 3 tabs colapsan a select horizontal scrollable y los
 * grids 2-cols pasan a 1-col vía la regla `.al-cli-modal-grid` en styles.css.
 */
import { useEffect, useState, type ChangeEvent } from "react";
import { Icon } from "../../lib/Icon";
import { ImageUploader } from "../../lib/ImageUploader";
import { useToast } from "../../lib/toast";
import { ApiError } from "../../api/client";
import { ClientMagicLinkModal } from "./ClientMagicLinkModal";
import { ClientMeasurementsPanel } from "./ClientMeasurementsPanel";
import {
  archiveClient,
  createClient,
  readLimitExceeded,
  restoreClient,
  updateClient,
  type ClientFull,
  type ClientCreatePayload,
  type ClientLimitExceededDetail,
} from "../../api/clients";

interface ClientFormModalProps {
  mode: "create" | "edit";
  /** En modo edit, el cliente existente. Ignorado en create. */
  client?: ClientFull;
  onClose: () => void;
  /** Callback con el cliente creado / actualizado / archivado / restaurado. */
  onSaved: (c: ClientFull) => void;
}

type StepId = 1 | 2 | 3 | 4;

/** Estado interno del form. Todos los campos como strings (los inputs HTML
 *  trabajan con strings); convertimos a number/null sólo al enviar. */
interface FormState {
  full_name: string;
  email: string;
  phone: string;
  photo_url: string;

  sex: "M" | "F" | "";
  birthdate: string;

  height_cm: string;
  initial_weight_kg: string;
  current_weight_kg: string;
  waist_cm: string;
  neck_cm: string;
  hip_cm: string;

  activity_level: string;
  goal: string;
  level: string;
  injuries: string;
  medical_notes: string;

  start_date: string;
  end_date: string;
  notes: string;
}

const EMPTY: FormState = {
  full_name: "",
  email: "",
  phone: "",
  photo_url: "",
  sex: "",
  birthdate: "",
  height_cm: "",
  initial_weight_kg: "",
  current_weight_kg: "",
  waist_cm: "",
  neck_cm: "",
  hip_cm: "",
  activity_level: "",
  goal: "",
  level: "",
  injuries: "",
  medical_notes: "",
  start_date: "",
  end_date: "",
  notes: "",
};

function fromClient(c: ClientFull): FormState {
  return {
    full_name: c.full_name || "",
    email: c.email || "",
    phone: c.phone || "",
    photo_url: c.photo_url || "",
    sex: (c.sex as "M" | "F") || "",
    birthdate: c.birthdate || "",
    height_cm: c.height_cm != null ? String(c.height_cm) : "",
    initial_weight_kg: c.initial_weight_kg != null ? String(c.initial_weight_kg) : "",
    current_weight_kg: c.current_weight_kg != null ? String(c.current_weight_kg) : "",
    waist_cm: c.waist_cm != null ? String(c.waist_cm) : "",
    neck_cm: c.neck_cm != null ? String(c.neck_cm) : "",
    hip_cm: c.hip_cm != null ? String(c.hip_cm) : "",
    activity_level: c.activity_level || "",
    goal: c.goal || "",
    level: c.level || "",
    injuries: c.injuries || "",
    medical_notes: c.medical_notes || "",
    start_date: c.start_date || "",
    end_date: c.end_date || "",
    notes: c.notes || "",
  };
}

function num(s: string): number | null {
  if (!s.trim()) return null;
  const n = parseFloat(s.replace(",", "."));
  return Number.isFinite(n) ? n : null;
}

/** Convierte el FormState al payload del backend, omitiendo strings vacíos. */
function toPayload(s: FormState): ClientCreatePayload {
  const out: ClientCreatePayload = { full_name: s.full_name.trim() };
  if (s.email.trim()) out.email = s.email.trim();
  if (s.phone.trim()) out.phone = s.phone.trim();
  if (s.photo_url.trim()) out.photo_url = s.photo_url.trim();
  if (s.sex) out.sex = s.sex;
  if (s.birthdate) out.birthdate = s.birthdate;
  // Altura: el campo espera CM, pero coaches a veces escriben metros (1.8).
  // Si el valor es < 3, asumimos metros y convertimos a cm (1.8 → 180). Ningún
  // adulto mide < 3 cm, así que el umbral es seguro.
  const h = num(s.height_cm); if (h != null) out.height_cm = h > 0 && h < 3 ? Math.round(h * 100) : h;
  const wi = num(s.initial_weight_kg); if (wi != null) out.initial_weight_kg = wi;
  const wc = num(s.current_weight_kg); if (wc != null) out.current_weight_kg = wc;
  const w = num(s.waist_cm); if (w != null) out.waist_cm = w;
  const n = num(s.neck_cm); if (n != null) out.neck_cm = n;
  const hi = num(s.hip_cm); if (hi != null) out.hip_cm = hi;
  if (s.activity_level) out.activity_level = s.activity_level as any;
  if (s.goal) out.goal = s.goal as any;
  if (s.level) out.level = s.level as any;
  if (s.injuries.trim()) out.injuries = s.injuries.trim();
  if (s.medical_notes.trim()) out.medical_notes = s.medical_notes.trim();
  if (s.start_date) out.start_date = s.start_date;
  if (s.end_date) out.end_date = s.end_date;
  if (s.notes.trim()) out.notes = s.notes.trim();
  return out;
}

export function ClientFormModal({ mode, client, onClose, onSaved }: ClientFormModalProps) {
  const toast = useToast();
  const [step, setStep] = useState<StepId>(1);
  const [form, setForm] = useState<FormState>(client ? fromClient(client) : EMPTY);
  const [saving, setSaving] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [limitInfo, setLimitInfo] = useState<ClientLimitExceededDetail | null>(null);
  const [showMagicLink, setShowMagicLink] = useState(false);

  // Sync si cambia el `client` externo (no debería pasar mientras el modal vive,
  // pero es defensivo si el padre re-pasa el mismo modal con otro cliente).
  useEffect(() => {
    if (client) setForm(fromClient(client));
  }, [client]);

  const canSave = form.full_name.trim().length > 0 && !saving;
  const isEdit = mode === "edit" && client != null;

  const setField = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm((s) => ({ ...s, [k]: v }));

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    setErrMsg(null);
    setLimitInfo(null);
    try {
      const payload = toPayload(form);
      let saved: ClientFull;
      if (isEdit && client) {
        saved = await updateClient(client.id, payload);
        toast(`Cliente "${saved.full_name}" actualizado`);
      } else {
        saved = await createClient(payload);
        toast(`Cliente "${saved.full_name}" creado`);
      }
      onSaved(saved);
    } catch (e) {
      const limit = readLimitExceeded(e);
      if (limit) {
        setLimitInfo(limit);
      } else {
        const msg = e instanceof ApiError ? e.message : (e instanceof Error ? e.message : String(e));
        setErrMsg(msg);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleArchive = async () => {
    if (!client) return;
    setArchiving(true);
    try {
      const saved = await archiveClient(client.id);
      toast(`"${saved.full_name}" archivado`);
      onSaved(saved);
    } catch (e) {
      toast("No pude archivar: " + (e instanceof Error ? e.message : ""));
    } finally {
      setArchiving(false);
    }
  };

  const handleRestore = async () => {
    if (!client) return;
    setRestoring(true);
    setLimitInfo(null);
    try {
      const saved = await restoreClient(client.id);
      toast(`"${saved.full_name}" restaurado`);
      onSaved(saved);
    } catch (e) {
      const limit = readLimitExceeded(e);
      if (limit) {
        setLimitInfo(limit);
      } else {
        toast("No pude restaurar: " + (e instanceof Error ? e.message : ""));
      }
    } finally {
      setRestoring(false);
    }
  };

  return (
    <div className="al-cli-modal-backdrop" onClick={onClose}>
      <div
        className="al-cli-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label={isEdit ? "Editar cliente" : "Nuevo cliente"}
      >
        <div className="al-cli-modal-head">
          <div>
            <div className="al-cli-modal-title">
              {isEdit ? "Editar cliente" : "Nuevo cliente"}
            </div>
            <div className="muted tiny">
              {isEdit ? client.full_name : "Crea un cliente para vincular sus planes y medidas"}
            </div>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={onClose} title="Cerrar">
            <Icon name="x" size={14} />
          </button>
        </div>

        <Stepper step={step} setStep={setStep} />

        {limitInfo && (
          <div className="al-cli-modal-banner al-cli-modal-banner--limit">
            <b>Has alcanzado el límite de tu plan {limitInfo.tier}.</b>
            <span className="muted">
              {" "}({limitInfo.current}/{limitInfo.limit} clientes activos).
              {limitInfo.suggested_tier
                ? ` Archiva uno o haz upgrade a ${limitInfo.suggested_tier}.`
                : " Archiva uno antes de crear más."}
            </span>
            <a
              href="mailto:nilton.alfonso@nilabs.co?subject=Upgrade%20AetherLite"
              className="btn btn-secondary btn-sm"
            >
              Ver opciones
            </a>
          </div>
        )}

        {errMsg && (
          <div className="al-cli-modal-banner al-cli-modal-banner--err">
            <b>No se pudo guardar.</b> <span className="muted">{errMsg}</span>
          </div>
        )}

        <div className="al-cli-modal-body">
          {step === 1 && <StepIdentity form={form} setField={setField} mode={mode} />}
          {step === 2 && <StepAnthro form={form} setField={setField} />}
          {step === 3 && <StepFitness form={form} setField={setField} />}
          {step === 4 && <StepMeasurements client={isEdit ? client : undefined} />}
        </div>

        <div className="al-cli-modal-foot">
          <div className="al-cli-modal-foot-left">
            {isEdit && client && client.is_active && (
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => setShowMagicLink(true)}
                disabled={saving || archiving}
                title="Generar un código para que el cliente entre a la app móvil"
              >
                📱 Link app móvil
              </button>
            )}
            {isEdit && client && client.is_active && (
              <button
                className="btn btn-ghost btn-sm"
                onClick={handleArchive}
                disabled={archiving || saving}
                title="Archivar (libera cuota del tier)"
              >
                {archiving ? "Archivando…" : "Archivar"}
              </button>
            )}
            {isEdit && client && !client.is_active && (
              <button
                className="btn btn-ghost btn-sm"
                onClick={handleRestore}
                disabled={restoring || saving}
                title="Restaurar (cuenta para el cap del tier)"
              >
                {restoring ? "Restaurando…" : "Restaurar"}
              </button>
            )}
          </div>
          <div className="al-cli-modal-foot-right">
            <button className="btn btn-ghost" onClick={onClose} disabled={saving}>
              Cancelar
            </button>
            {step < 4 && mode === "edit" && (
              <button className="btn btn-secondary" onClick={() => setStep((s) => (s + 1) as StepId)}>
                Siguiente
              </button>
            )}
            <button className="btn btn-primary" onClick={handleSave} disabled={!canSave}>
              {saving
                ? "Guardando…"
                : isEdit
                ? "Guardar cambios"
                : "Crear cliente"}
            </button>
          </div>
        </div>
      </div>

      {showMagicLink && client && (
        <ClientMagicLinkModal client={client} onClose={() => setShowMagicLink(false)} />
      )}
    </div>
  );
}

// ---------------- Stepper ----------------

function Stepper({ step, setStep }: { step: StepId; setStep: (s: StepId) => void }) {
  const STEPS: { id: StepId; label: string }[] = [
    { id: 1, label: "Identidad" },
    { id: 2, label: "Antropometría" },
    { id: 3, label: "Fitness" },
    { id: 4, label: "Medidas" },
  ];
  return (
    <div className="al-cli-stepper" role="tablist">
      {STEPS.map((s) => (
        <button
          key={s.id}
          role="tab"
          aria-selected={step === s.id}
          className={"al-cli-step " + (step === s.id ? "is-active" : "")}
          onClick={() => setStep(s.id)}
        >
          <span className="al-cli-step-num">{s.id}</span>
          <span className="al-cli-step-lbl">{s.label}</span>
        </button>
      ))}
    </div>
  );
}

// ---------------- Step 1: Identidad ----------------

interface StepProps {
  form: FormState;
  setField: <K extends keyof FormState>(k: K, v: FormState[K]) => void;
}

function StepIdentity({ form, setField, mode }: StepProps & { mode: "create" | "edit" }) {
  const onChange = (k: keyof FormState) => (e: ChangeEvent<HTMLInputElement>) =>
    setField(k as any, e.target.value as any);

  return (
    <div className="al-cli-form">
      <div className="field">
        <label>
          Nombre completo <span className="al-cli-req">*</span>
        </label>
        <input
          autoFocus={mode === "create"}
          value={form.full_name}
          onChange={onChange("full_name")}
          placeholder="Ej: Andrés Pérez"
        />
        <span className="hint">Único campo obligatorio. Puedes completar el resto luego.</span>
      </div>

      <div className="al-cli-modal-grid">
        <div className="field">
          <label>Email</label>
          <input
            type="email"
            value={form.email}
            onChange={onChange("email")}
            placeholder="nombre@correo.com"
          />
        </div>
        <div className="field">
          <label>Teléfono</label>
          <input
            type="tel"
            value={form.phone}
            onChange={onChange("phone")}
            placeholder="+57 300 000 0000"
          />
        </div>
      </div>

      <div className="field">
        <label>Foto del cliente</label>
        <ImageUploader
          value={form.photo_url || null}
          onChange={(url) => setField("photo_url", url || "")}
          folder="client"
          shape="circle"
          size={100}
          label="Subir foto"
        />
        <span className="hint">JPEG / PNG / WebP, máx 5 MB. Recortable.</span>
      </div>

      <div className="al-cli-modal-grid">
        <div className="field">
          <label>Sexo</label>
          <div className="al-cli-radio">
            <label>
              <input
                type="radio"
                name="cli-sex"
                checked={form.sex === "M"}
                onChange={() => setField("sex", "M")}
              />{" "}
              Hombre
            </label>
            <label>
              <input
                type="radio"
                name="cli-sex"
                checked={form.sex === "F"}
                onChange={() => setField("sex", "F")}
              />{" "}
              Mujer
            </label>
          </div>
        </div>
        <div className="field">
          <label>Fecha de nacimiento</label>
          <input
            type="date"
            value={form.birthdate}
            onChange={onChange("birthdate")}
          />
        </div>
      </div>
    </div>
  );
}

// ---------------- Step 2: Antropometría ----------------

function StepAnthro({ form, setField }: StepProps) {
  const onChange = (k: keyof FormState) => (e: ChangeEvent<HTMLInputElement>) =>
    setField(k as any, e.target.value as any);

  return (
    <div className="al-cli-form">
      <div className="al-cli-modal-tip">
        Estos datos los hereda la calculadora antropométrica del menú lateral
        cuando seleccionas este cliente.
      </div>

      <div className="al-cli-modal-grid">
        <div className="field">
          <label>Estatura (cm)</label>
          <input
            type="number"
            inputMode="decimal"
            value={form.height_cm}
            onChange={onChange("height_cm")}
            placeholder="Ej. 180"
          />
          {(() => {
            const h = parseFloat((form.height_cm || "").replace(",", "."));
            return Number.isFinite(h) && h > 0 && h < 3 ? (
              <span className="al-cli-field-hint">
                Lo guardamos como <b>{Math.round(h * 100)} cm</b> (escribiste la estatura en metros).
              </span>
            ) : null;
          })()}
        </div>
        <div className="field">
          <label>Peso inicial (kg)</label>
          <input type="number" inputMode="decimal" value={form.initial_weight_kg} onChange={onChange("initial_weight_kg")} />
        </div>
      </div>

      <div className="al-cli-modal-grid">
        <div className="field">
          <label>Peso actual (kg)</label>
          <input type="number" inputMode="decimal" value={form.current_weight_kg} onChange={onChange("current_weight_kg")} />
        </div>
        <div className="field">
          <label>Cintura (cm)</label>
          <input type="number" inputMode="decimal" value={form.waist_cm} onChange={onChange("waist_cm")} />
        </div>
      </div>

      <div className="al-cli-modal-grid">
        <div className="field">
          <label>Cuello (cm)</label>
          <input type="number" inputMode="decimal" value={form.neck_cm} onChange={onChange("neck_cm")} />
          <span className="hint">Necesario para % grasa US Navy</span>
        </div>
        <div className="field">
          <label>Cadera (cm)</label>
          <input type="number" inputMode="decimal" value={form.hip_cm} onChange={onChange("hip_cm")} />
          <span className="hint">Sólo necesario para mujeres (US Navy)</span>
        </div>
      </div>
    </div>
  );
}

// ---------------- Step 3: Fitness ----------------

function StepFitness({ form, setField }: StepProps) {
  const onText = (k: keyof FormState) => (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setField(k as any, e.target.value as any);
  const onSel = (k: keyof FormState) => (e: ChangeEvent<HTMLSelectElement>) =>
    setField(k as any, e.target.value as any);

  return (
    <div className="al-cli-form">
      <div className="al-cli-modal-grid">
        <div className="field">
          <label>Nivel de actividad</label>
          <select value={form.activity_level} onChange={onSel("activity_level")}>
            <option value="">—</option>
            <option value="sedentary">Sedentario</option>
            <option value="light">Ligero (1-3 días/sem)</option>
            <option value="moderate">Moderado (3-5 días/sem)</option>
            <option value="intense">Intenso (6-7 días/sem)</option>
            <option value="athlete">Atleta (2x/día)</option>
          </select>
        </div>
        <div className="field">
          <label>Objetivo</label>
          <select value={form.goal} onChange={onSel("goal")}>
            <option value="">—</option>
            <option value="maintain">Mantener</option>
            <option value="deficit">Déficit (perder grasa)</option>
            <option value="surplus">Superávit (ganar)</option>
            <option value="recomp">Recomposición</option>
          </select>
        </div>
      </div>

      <div className="field">
        <label>Nivel de experiencia</label>
        <select value={form.level} onChange={onSel("level")}>
          <option value="">—</option>
          <option value="beginner">Principiante</option>
          <option value="intermediate">Intermedio</option>
          <option value="advanced">Avanzado</option>
        </select>
      </div>

      <div className="field">
        <label>Lesiones / limitaciones</label>
        <textarea
          rows={2}
          value={form.injuries}
          onChange={onText("injuries")}
          placeholder="Ej: hernia lumbar, manguito rotador derecho…"
        />
      </div>

      <div className="field">
        <label>Notas médicas</label>
        <textarea
          rows={2}
          value={form.medical_notes}
          onChange={onText("medical_notes")}
          placeholder="Medicación, alergias, condiciones relevantes…"
        />
      </div>

      <div className="al-cli-modal-grid">
        <div className="field">
          <label>Inicio del programa</label>
          <input type="date" value={form.start_date} onChange={onText("start_date") as any} />
        </div>
        <div className="field">
          <label>Fin del programa</label>
          <input type="date" value={form.end_date} onChange={onText("end_date") as any} />
        </div>
      </div>

      <div className="field">
        <label>Notas generales</label>
        <textarea
          rows={4}
          value={form.notes}
          onChange={onText("notes")}
          placeholder="Cualquier observación libre que ayude al coach…"
        />
      </div>
    </div>
  );
}

// ---------------- Step 4: Medidas ----------------

/**
 * En modo EDIT (cliente con id) renderiza el panel de medidas completo.
 * En modo CREATE el cliente todavía no existe en backend, así que las medidas
 * no se pueden listar ni crear — mostramos un placeholder pidiendo guardar.
 */
function StepMeasurements({ client }: { client?: ClientFull }) {
  if (!client) {
    return (
      <div className="al-cli-form">
        <div className="al-meas-cli-locked">
          <div className="al-meas-cli-locked-ic">
            <Icon name="sparkle" size={26} />
          </div>
          <div className="al-meas-cli-locked-title">Guarda el cliente primero</div>
          <p className="muted">
            Las medidas corporales se registran sobre un cliente existente. Crea
            el cliente con el botón de abajo y vuelve a este paso para añadir su
            primera toma.
          </p>
        </div>
      </div>
    );
  }
  return (
    <div className="al-cli-form">
      <ClientMeasurementsPanel clientId={client.id} />
    </div>
  );
}

